import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { onMutateSuccess, onQuerySuccess } from '../../../utils/queryResponse';
import { deleteBadge, getLastIssuedBadge, searchBadges, voidBadge } from '../../../services/badges';
import { tagLabel } from '../../../constants/labels';
import OverlayLoader from '../../../components/overlayLoader';
import SetBadgeModal from './badgeSet';
import ConfirmationModal from '../../../components/modals/confirmation';

const BadgesPage = () => {

    const { t } = useTranslation();

    const [setBadgeModal, setSetBadgeModal] = useState(false)
    const [confirmationModal, setConfirmationModal] = useState(false)
    const [voidModal, setVoidModal] = useState(false)

    const [badges, setBadges] = useState([])
    const [selectedBadge, setSelectedBadge] = useState()
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [lastIssued, setLastIssued] = useState(null)

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300)
        return () => clearTimeout(t)
    }, [search])

    const { data, isFetching } = useQuery(['badges', debouncedSearch], () => searchBadges(debouncedSearch), { keepPreviousData: true })
    const { data: dbLastIssued } = useQuery(['badges', 'last-issued'], getLastIssuedBadge)

    const queryClient = useQueryClient()
    const invalidate = () => {
        queryClient.invalidateQueries(['badges'])
        queryClient.invalidateQueries(['available-badges'])
    }
    const { mutate, isLoading: isDeleting } = useMutation(deleteBadge, {
        onSuccess: onMutateSuccess(invalidate)
    })
    const { mutate: mutateVoid, isLoading: isVoiding } = useMutation(voidBadge, {
        onSuccess: onMutateSuccess(invalidate, t('settingsPage.tagMarkedVoid'))
    })

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setBadges(Array.isArray(result) ? result : [])
        })
    }, [data])

    useEffect(() => {
        onQuerySuccess(dbLastIssued, (result) => setLastIssued(result))
    }, [dbLastIssued])

    const handleSetBadge = (badge) => {
        setSelectedBadge(badge)
        setSetBadgeModal(true)
    }

    const handleConfirmation = (badge) => {
        setSelectedBadge(badge)
        setConfirmationModal(true)
    }

    const handleVoidConfirmation = (badge) => {
        setSelectedBadge(badge)
        setVoidModal(true)
    }

    const handleDeleteBadge = () => {
        mutate(selectedBadge.id)
        setSelectedBadge()
    }

    const handleVoidBadge = () => {
        mutateVoid({ id: selectedBadge.id, reason: 'Voided from the Tags page' })
        setSelectedBadge()
    }

    const chipStyle = (badge) => {
        const isVoid = badge.status === 'VOID'
        return {
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: isVoid ? 'var(--surface)' : (badge.assigned ? 'var(--brand-50)' : '#ecfdf5'),
            border: `1px solid ${isVoid ? 'var(--line)' : (badge.assigned ? 'var(--brand-100)' : '#a7f3d0')}`,
            borderRadius: '20px', padding: '6px 10px 6px 14px',
            fontSize: '13px', fontWeight: 700,
            color: isVoid ? 'var(--muted)' : 'var(--brand-ink)',
            textDecoration: isVoid ? 'line-through' : 'none',
            boxShadow: 'var(--sh-1)',
        }
    }

    const statusChip = (badge) => {
        const isVoid = badge.status === 'VOID'
        const label = isVoid ? t('settingsPage.voidStatus') : (badge.assigned ? t('settingsPage.assignedStatus') : t('settingsPage.freeStatus'))
        const bg = isVoid ? 'rgba(107,114,128,.15)' : (badge.assigned ? 'rgba(37,99,235,.12)' : 'rgba(16,185,129,.15)')
        return (
            <span title={isVoid && badge.voidReason ? badge.voidReason : (badge.jobIds?.length ? t('settingsPage.tagJobs', { count: badge.jobIds.length, ids: badge.jobIds.join(', #') }) : '')}
                style={{ background: bg, borderRadius: '10px', padding: '1px 7px', fontSize: '11px', fontWeight: 700, textDecoration: 'none' }}>
                {label}
            </span>
        )
    }

    return (
        <div className="sbr">
            <ConfirmationModal display={confirmationModal} toggle={setConfirmationModal} title={t('settingsPage.deleteTag')}
                message={t('settingsPage.confirmDeleteTag')} confirmAction={handleDeleteBadge} />
            <ConfirmationModal display={voidModal} toggle={setVoidModal} title={t('settingsPage.voidTag')}
                message={t('settingsPage.confirmVoidTag', { tag: tagLabel(selectedBadge) })} confirmAction={handleVoidBadge} />
            <SetBadgeModal display={setBadgeModal} toggle={setSetBadgeModal} selectedBadge={selectedBadge ?? ''} lastIssued={lastIssued} />

            {/* Page header */}
            <div className="dhead">
                <div>
                    <div className="ttl">{t('settingsPage.tagsTitle')}</div>
                    <div className="sub">
                        {t('settingsPage.tagsPageDesc')}
                        {lastIssued && (
                            <> {' '}{t('settingsPage.lastIssued')} <b>{lastIssued.last ? tagLabel(lastIssued.last) : '—'}</b> · {t('settingsPage.next')} <b>{lastIssued.next?.code}</b></>
                        )}
                    </div>
                </div>
                <div className="act" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input type="search" className="form-control" style={{ width: '200px' }} placeholder={t('settingsPage.searchAllTags')}
                        value={search} onChange={(e) => setSearch(e.target.value)} />
                    <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => handleSetBadge()}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        {t('settingsPage.issueTags')}
                    </button>
                </div>
            </div>

            {/* Tag list */}
            <div className="card-sbr" style={{ position: 'relative', minHeight: '80px' }}>
                {isFetching && badges.length === 0 ? (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {[1, 2, 3, 4].map((n) => (
                            <div key={n} style={{
                                width: '100px', height: '36px', borderRadius: '20px',
                                background: 'var(--surface)', border: '1px solid var(--line)',
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }} />
                        ))}
                    </div>
                ) : badges.length === 0 ? (
                    <div className="sbr-empty">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="8" r="6"/><path d="M8.56 2.9 7 1M15.44 2.9 17 1M12 14l-4 7h8l-4-7z"/>
                        </svg>
                        {debouncedSearch ? t('settingsPage.noTagsMatch') : t('settingsPage.noTagsFound')}
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {badges.map((badge) => (
                            <span key={badge.id} style={chipStyle(badge)}>
                                <span style={{ cursor: badge.code ? 'default' : 'pointer' }}
                                    title={badge.code ? undefined : t('settingsPage.editLegacyTag')}
                                    onClick={() => !badge.code && handleSetBadge(badge)}>
                                    {t('settingsPage.tag')} {tagLabel(badge)}
                                </span>
                                {statusChip(badge)}
                                {badge.status !== 'VOID' && (
                                    <button title={t('settingsPage.voidThisTag')} onClick={() => handleVoidConfirmation(badge)}
                                        style={{
                                            background: 'rgba(220,38,38,.10)', border: 'none', borderRadius: '50%',
                                            padding: '3px', cursor: 'pointer', color: '#b91c1c',
                                            display: 'flex', alignItems: 'center', lineHeight: 1,
                                            width: '20px', height: '20px', justifyContent: 'center',
                                        }}>
                                        <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="9"/><line x1="5.5" y1="5.5" x2="18.5" y2="18.5"/>
                                        </svg>
                                    </button>
                                )}
                                {!badge.assigned && (
                                    <button title={t('settingsPage.remove')} onClick={() => handleConfirmation(badge)}
                                        style={{
                                            background: 'rgba(37,99,235,.10)', border: 'none', borderRadius: '50%',
                                            padding: '3px', cursor: 'pointer', color: 'var(--brand-ink)',
                                            display: 'flex', alignItems: 'center', lineHeight: 1,
                                            width: '20px', height: '20px', justifyContent: 'center',
                                        }}>
                                        <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                        </svg>
                                    </button>
                                )}
                            </span>
                        ))}
                    </div>
                )}
                {(isFetching || isDeleting || isVoiding) && badges.length > 0 && <OverlayLoader />}
            </div>
        </div>
    );
};

export default BadgesPage;
